// ============================================================
// components/stashcard/HistoryTimeline.tsx
//
// "Your conversation with {vendor}" — the merged log of calls,
// texts, emails, web visits, map opens, and user notes. Sorted
// newest-first. Empty state included.
// ============================================================

import { useState } from 'react';
import { Filter, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HistoryEntry } from './HistoryEntry';
import { NoteComposer } from './NoteComposer';
import type { HistoryItem, HistoryKind } from '../../store/stash-card-detail/types';
import { cn } from '@/lib/utils';

interface HistoryTimelineProps {
  /** Vendor display name for the section header — "Your conversation with X". */
  vendorName: string;
  items: HistoryItem[];
  /** Render the inline composer above the list. */
  showComposer?: boolean;
  /** Pass `now` for deterministic test output. */
  now?: Date;
  className?: string;
}

const FILTERS: { key: HistoryKind | 'all'; label: string }[] = [
  { key: 'all',  label: 'All' },
  { key: 'call', label: 'Calls' },
  { key: 'text', label: 'Texts' },
  { key: 'web',  label: 'Web' },
  { key: 'note', label: 'Notes' },
];

export function HistoryTimeline({
  vendorName,
  items,
  showComposer = true,
  now,
  className,
}: HistoryTimelineProps): React.JSX.Element {
  const [filter, setFilter] = useState<HistoryKind | 'all'>('all');

  const filtered = filter === 'all' ? items : items.filter(i => i.kind === filter);

  return (
    <section
      aria-label={`History for ${vendorName}`}
      className={cn('flex flex-col gap-3', className)}
    >
      {/* Header */}
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground">
            Your conversation with {vendorName}
          </p>
          <h2 className="mt-1 text-xl font-bold tracking-tight">
            History <span className="font-medium text-muted-foreground">· {items.length}</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter size={14} />
            <span className="ml-1">Filter</span>
          </Button>
          <Button variant="default" size="sm">
            <Plus size={14} />
            <span className="ml-1">Add note</span>
          </Button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {FILTERS.map(f => {
          const isActive = filter === f.key;
          const count =
            f.key === 'all' ? items.length : items.filter(i => i.kind === f.key).length;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                'shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                isActive
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label}
              <span className="ml-1.5 opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Composer */}
      {showComposer && <NoteComposer />}

      {/* List */}
      {filtered.length === 0 ? (
        <EmptyHistory hasAnyHistory={items.length > 0} filterLabel={FILTERS.find(f => f.key === filter)?.label ?? 'All'} />
      ) : (
        <ol className="flex flex-col gap-2" role="list">
          {filtered.map(item => (
            <li key={item.id}>
              <HistoryEntry item={item} {...(now ? { now } : {})} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function EmptyHistory({ hasAnyHistory, filterLabel }: { hasAnyHistory: boolean; filterLabel: string }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-border bg-card p-8 text-center">
      <p className="text-sm font-semibold text-foreground">
        {hasAnyHistory ? `No ${filterLabel.toLowerCase()} yet` : 'No history yet'}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {hasAnyHistory
          ? `Try a different filter — or log the next interaction below.`
          : `Reach out to the vendor — every call, text, and visit gets logged here.`}
      </p>
    </div>
  );
}
